// SA Feature Flags & Numeric Overrides editor for an org.
// Renders inside SAOrgDetail as a separate card.
//
// 3-state design:
//   true  → force ENABLE
//   false → force DISABLE
//   null  → use plan default
import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Flag, RefreshCw, Save, Loader2, HardDrive, Sliders,
  CheckCircle2, XCircle, MinusCircle,
} from 'lucide-react';

const MODULE_DEFS = [
  { key: 'docs', label: 'Docs / Pages', desc: 'Notion-lite per-project knowledge base' },
  { key: 'whiteboard', label: 'Whiteboard', desc: 'React Flow canvas with shapes & connectors' },
  { key: 'timeline', label: 'Timeline / Gantt', desc: 'Sprint + dependency timeline view' },
  { key: 'integrations_slack', label: 'Slack Integration', desc: 'OAuth-connected Slack channels & inbound commands' },
  { key: 'integrations_teams', label: 'Microsoft Teams', desc: 'OAuth-connected Teams channels' },
  { key: 'csv_import_export', label: 'CSV Import / Export', desc: 'Bulk task import + project export' },
  { key: 'service_desk', label: 'Service Desk add-on', desc: 'Branded help portals + ticket queues (₹600/user/mo add-on)' },
];

const NUMERIC_DEFS = [
  { key: 'storage_quota_mb', label: 'Storage quota (MB)', help: 'Leave blank to use plan default (Free=100, Business=10240)' },
  { key: 'seat_limit', label: 'Seat limit override', help: 'Leave blank to use subscription seats' },
  { key: 'max_projects', label: 'Max projects', help: 'Leave blank for unlimited' },
  { key: 'max_pages_per_project', label: 'Max pages per project', help: 'Free=0, Business=15. Leave blank for default.' },
  { key: 'max_whiteboards_per_project', label: 'Max whiteboards per project', help: 'Free=0, Business=5. Leave blank for default.' },
  { key: 'max_upload_size_mb', label: 'Max single-upload size (MB)', help: 'Free=5, Business=25. Leave blank for default.' },
];

function fmtBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Three-way toggle pill — Default / Enabled / Disabled
function TriToggle({ value, onChange, testId }) {
  const states = [
    { v: null, label: 'Default', icon: MinusCircle, cls: 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200' },
    { v: true, label: 'Enabled', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' },
    { v: false, label: 'Disabled', icon: XCircle, cls: 'bg-red-100 text-red-700 hover:bg-red-200' },
  ];
  return (
    <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden" data-testid={testId}>
      {states.map(s => {
        const Icon = s.icon;
        const active = value === s.v;
        return (
          <button
            key={String(s.v)}
            type="button"
            onClick={() => onChange(s.v)}
            className={`px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1 transition ${active ? s.cls : 'text-zinc-400 hover:bg-zinc-50'}`}
            data-testid={`${testId}-${s.label.toLowerCase()}`}
          >
            <Icon size={11} /> {s.label}
          </button>
        );
      })}
    </div>
  );
}

export default function SAFeatureFlagsTab({ orgId }) {
  const { apiFetch } = useSuperAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [flags, setFlags] = useState({});
  const [overrides, setOverrides] = useState({});
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}/flags`);
      if (!res.ok) { toast.error('Failed to load flags'); return; }
      const d = await res.json();
      setData(d);
      setFlags(d.flags || {});
      setOverrides(d.overrides || {});
      setNotes(d.notes || '');
      setDirty(false);
    } finally { setLoading(false); }
  }, [apiFetch, orgId]);

  useEffect(() => { load(); }, [load]);

  const updateFlag = (key, val) => { setFlags(f => ({ ...f, [key]: val })); setDirty(true); };
  const updateOverride = (key, val) => {
    setOverrides(o => {
      const next = { ...o };
      if (val === '' || val === null || val === undefined) delete next[key];
      else next[key] = Number(val);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip null flag values before sending — null === "use default" and we keep storage tidy.
      const cleanFlags = Object.fromEntries(Object.entries(flags).filter(([, v]) => v === true || v === false));
      const res = await apiFetch(`/organizations/${orgId}/flags`, {
        method: 'PUT',
        body: JSON.stringify({ flags: cleanFlags, overrides, notes }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Save failed');
        return;
      }
      toast.success('Flags saved — changes apply on next request');
      load();
    } finally { setSaving(false); }
  };

  const recomputeStorage = async () => {
    setRecomputing(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/storage/recompute`, { method: 'POST' });
      if (!res.ok) { toast.error('Recompute failed'); return; }
      toast.success('Storage recomputed');
      load();
    } finally { setRecomputing(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  if (!data) return null;

  const storage = data.storage || {};
  const usedPct = storage.quota_bytes > 0 ? Math.round((storage.used_bytes / storage.quota_bytes) * 100) : 0;
  const planDefaults = data.plan_defaults || {};

  return (
    <div className="space-y-4" data-testid="sa-flags-tab">
      {/* STORAGE CARD */}
      <Card className="border-slate-200/60">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><HardDrive className="w-4 h-4" /> Storage</CardTitle>
          <Button size="sm" variant="outline" onClick={recomputeStorage} disabled={recomputing} className="h-7 text-[11px]" data-testid="sa-storage-recompute">
            {recomputing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Recompute
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between mb-2">
            <div>
              <div className="text-[22px] font-black tabular-nums text-slate-900">{fmtBytes(storage.used_bytes)}</div>
              <div className="text-[11px] text-slate-500">{storage.file_count?.toLocaleString() || 0} file(s) · of {fmtBytes(storage.quota_bytes)}</div>
            </div>
            <div className={`text-[11px] font-bold tabular-nums ${usedPct >= 95 ? 'text-red-600' : usedPct >= 80 ? 'text-amber-600' : 'text-emerald-600'}`}>{usedPct}%</div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${usedPct >= 95 ? 'bg-red-500' : usedPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(100, usedPct)}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* MODULE FLAGS */}
      <Card className="border-slate-200/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Flag className="w-4 h-4" /> Module flags</CardTitle>
          <p className="text-[11px] text-slate-500 pt-1">
            <strong>Default</strong> uses the plan's normal behavior. <strong>Enabled</strong> forces the feature ON regardless of plan. <strong>Disabled</strong> turns it OFF for this org.
          </p>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {MODULE_DEFS.map(m => {
            const planDefault = planDefaults[m.key];
            return (
              <div key={m.key} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`sa-flag-row-${m.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-slate-800">{m.label}</span>
                    <Badge variant="outline" className="text-[9px] py-0 px-1.5">
                      Plan default: {planDefault === true ? 'ON' : planDefault === false ? 'OFF' : '—'}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{m.desc}</p>
                </div>
                <TriToggle
                  value={flags[m.key] === undefined ? null : flags[m.key]}
                  onChange={v => updateFlag(m.key, v)}
                  testId={`sa-flag-${m.key}`}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* NUMERIC OVERRIDES */}
      <Card className="border-slate-200/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Sliders className="w-4 h-4" /> Numeric overrides</CardTitle>
          <p className="text-[11px] text-slate-500 pt-1">Empty value = use plan default. Set a number to override per-org.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {NUMERIC_DEFS.map(n => (
            <div key={n.key}>
              <Label className="text-[11px] text-slate-500 flex items-center justify-between">
                <span>{n.label}</span>
                <span className="text-[10px] text-slate-400 tabular-nums">default: {planDefaults[n.key] ?? '—'}</span>
              </Label>
              <Input
                type="number"
                min={0}
                value={overrides[n.key] ?? ''}
                onChange={e => updateOverride(n.key, e.target.value)}
                placeholder={`Plan default${planDefaults[n.key] !== undefined ? ` (${planDefaults[n.key]})` : ''}`}
                className="mt-1 h-9 text-sm"
                data-testid={`sa-override-${n.key}`}
              />
              <p className="text-[10px] text-slate-400 mt-1">{n.help}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* AUDIT NOTES */}
      <Card className="border-slate-200/60">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Notes (audit trail)</CardTitle></CardHeader>
        <CardContent>
          <textarea
            value={notes}
            onChange={e => { setNotes(e.target.value); setDirty(true); }}
            placeholder="e.g. Acme Corp negotiated 10GB storage in Q2 contract. Slack disabled for compliance."
            rows={3}
            maxLength={1000}
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-slate-400"
            data-testid="sa-flags-notes"
          />
          {data.updated_at && (
            <p className="text-[10px] text-slate-400 mt-1">
              Last updated {new Date(data.updated_at).toLocaleString()} {data.updated_by ? `by ${data.updated_by}` : ''}
            </p>
          )}
        </CardContent>
      </Card>

      {/* SAVE BAR */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-6 px-6 py-3 flex items-center justify-between">
        <span className="text-[11px] text-slate-500">{dirty ? 'Unsaved changes' : 'No changes'}</span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={saving} className="h-8 text-[12px]" data-testid="sa-flags-cancel">
            Discard
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !dirty} className="h-8 text-[12px] bg-slate-900 hover:bg-slate-800" data-testid="sa-flags-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save flags</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
